define((require) => {
    "use strict";

    const _                             = require("underscore");
    const AbstractWidgetView            = require("shared/ui/experience/widgets/AbstractWidgetView");
    const CompositeWidgetLayout         = require("shared/ui/experience/widgets/compositeWidget/CompositeWidgetLayout");
    const CompositeWidgetCollectionView = require("shared/ui/experience/widgets/compositeWidget/CompositeWidgetCollectionView");
    const {ETW_IS_READY}                 = require("editor/ui/experience/widgets/textEnhancedWidget/textWidgetEvents");

    /*
    * This composite view renders grouped widgets using a collection view inside a layout region
    */

    return AbstractWidgetView.extend("CompositeWidgetView", {

        className: `${AbstractWidgetView.prototype.className} composite-widget`,

        groupCollection: null,

        groupCollectionView: null,

        _layout: null,

        onInitialize() {
            this.groupCollection = this.model.get("group");
        },

        showView() {
            if (this.groupCollection) {
                this._renderLayout();
            }

            this.__super();

            if (!this.options.edited) {
                _.defer(this._makeElementUnreachable.bind(this));
            }
        },

        // Legacy browsers (LTE IE10) do not support pointer-events styling
        // meaning that the click events can't be delegated to child elements.
        // Make the composite widget unreachable so underlying elements are interactive.
        _makeElementUnreachable() {
            this.$el.css("width", 0);
        },

        _setGroupedWidgetCollectionView() {
            this.groupCollectionView = new CompositeWidgetCollectionView(_.extend(this.options, {
                collection: this.groupCollection,
                container: this._layout.group,
                compositeModel: this.model
            }));
        },

        /**
         * A translation hook to this model is added to map all translations made
         * to this view model to each widget to compensate for position offsets
         */
        _showGroupedWidgetCollectionView() {
            if (!this.groupCollectionView) {
                this._setGroupedWidgetCollectionView();
            }

            // Pass on the `viewScope` manually due to the "layout strategy" used in here. The view scope concept
            // relies on Marionette regions (see our `Region.js`), i.e. a layout/view needs to be added to a
            // region for the view scope to work out of the box.
            this.groupCollectionView.viewScope = this.viewScope;

            // Defer rendering due to other deferred renderings (e.g. TextWidgetViewEditorMixin)
            this.listenTo(this.groupCollectionView, "collection:rendered", () => {
                _.defer(() => {
                    if (this.options.edited) {
                        this.transformToBoundingBox();
                    } else if (this._active) {
                        this.groupCollectionView.updateGroupActiveState();
                    }
                });
            });

            this._layout.group.show(this.groupCollectionView);
        },

        _renderLayout() {
            this._layout = new CompositeWidgetLayout({el: this.$el});
            this._layout.render();
            this._showGroupedWidgetCollectionView();
        },
        /**
         * Update current model position and size to fit
         * the bounding box of all of its grouped widgets
         */
        transformToBoundingBox() {
            const experience = this.options.viewModel.get("experience");
            const boundingBoxProps = this.groupCollectionView.getGroupBoundingBox();

            this.model.setSizeAndPosition(boundingBoxProps);

            // Listen for scene changes and update the boundingBox if its size has changed
            // E.g. this is relevant for the scene navigator widget which dynamically changes size
            this.listenTo(experience, "add:scenes remove:scenes", () => {
                this._updateBoundingBox(
                    boundingBoxProps,
                    this.groupCollectionView.getGroupBoundingBox()
                );
            });
            this.groupCollection.each((widget) => {
                this.listenTo(widget, "transformed", () => {
                    const newBoundingboxProps = this.groupCollectionView.getGroupBoundingBox();
                    if (!_.isEqual(newBoundingboxProps, boundingBoxProps)) {
                        this.model.setSizeAndPosition(newBoundingboxProps, true);
                    }
                });
            });

            this.listenTo(this.options.viewModel, ETW_IS_READY, () => {
                this._updateBoundingBox(
                    boundingBoxProps,
                    this.groupCollectionView.getGroupBoundingBox()
                );
            });
        },

        _updateBoundingBox(boundingBoxProps, newBoundingBoxProps) {
            if (!_.isEqual(newBoundingBoxProps, boundingBoxProps)) {
                this.model.setSizeAndPosition(newBoundingBoxProps, true);
            }
        },

        /**
         * When this composite widget is set to active, update active state of children views
         */
        setActive(active) {
            this._active = active;

            if (this.groupCollectionView) {
                this.groupCollectionView.updateGroupActiveState(active);
            }

            this.__super();
        }
    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/compositeWidget/CompositeWidgetView.js